import * as OtelTracer from '@effect/opentelemetry/Tracer'
import { Cause, type Context, Deferred, Duration, Effect, Fiber, pipe, Scope, type Stream } from 'effect'
import type { UnknownException } from 'effect/Cause'
import { log } from 'effect/Console'
import { dual, type LazyArg } from 'effect/Function'
import type { Predicate, Refinement } from 'effect/Predicate'

import { isDevEnv, isPromise, objectToString } from '../mod.ts'
import { UnknownError } from './Error.ts'

export * from 'effect/Effect'
export { spanEvent } from './spanEvent.ts'

// export const log = <A>(message: A, ...rest: any[]): Effect.Effect<void> =>
//   Effect.sync(() => {
//     console.log(message, ...rest)
//   })

// export const logWarn = <A>(message: A, ...rest: any[]): Effect.Effect<void> =>
//   Effect.sync(() => {
//     console.warn(message, ...rest)
//   })

// export const logError = <A>(message: A, ...rest: any[]): Effect.Effect<void> =>
//   Effect.sync(() => {
//     console.error(message, ...rest)
//   })

/** Same as `Effect.scopeWith` but with a `CloseableScope` instead of a `Scope`. */
export const scopeWithCloseable = <R, E, A>(
  fn: (scope: Scope.CloseableScope) => Effect.Effect<A, E, R | Scope.Scope>,
): Effect.Effect<A, E, R | Scope.Scope> =>
  Effect.gen(function* () {
    // const parentScope = yield* Scope.Scope
    // const scope = yield* Scope.fork(parentScope, ExecutionStrategy.sequential)
    const scope = yield* Scope.make()
    yield* Effect.addFinalizer((exit) => Scope.close(scope, exit))
    return yield* fn(scope).pipe(Scope.extend(scope))
  })

export type SyncOrPromiseOrEffect<TResult, TError = never, TContext = never> =
  | TResult
  | Promise<TResult>
  | Effect.Effect<TResult, TError, TContext>

export const tryAll = <Res>(
  fn: () => Res,
): Res extends Effect.Effect<infer A, infer E>
  ? Effect.Effect<A, E | UnknownException>
  : Res extends Promise<infer A>
    ? Effect.Effect<A, UnknownException>
    : Effect.Effect<Res, UnknownException> =>
  Effect.try(() => fn()).pipe(
    Effect.andThen((fnRes) =>
      Effect.isEffect(fnRes) === true
        ? (fnRes as any as Effect.Effect<any>)
        : isPromise(fnRes) === true
          ? Effect.promise(() => fnRes)
          : Effect.succeed(fnRes),
    ),
  ) as any

export const acquireReleaseLog = (label: string) =>
  Effect.acquireRelease(Effect.log(`${label} acquire`), (_, ex) => Effect.log(`${label} release`, ex))

export const addFinalizerLog = (...msgs: any[]) =>
  Effect.addFinalizer((exit) =>
    Effect.log(...msgs, exit._tag === 'Success' ? 'with success' : `with failure: ${Cause.pretty(exit.cause)}`),
  )

export const logBefore =
  (...msgs: any[]) =>
  <A, E, R>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.andThen(Effect.log(...msgs), eff)

/** Logs both on errors and defects */
export const tapCauseLogPretty = <R, E, A>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
  Effect.tapErrorCause(eff, (cause) =>
    Effect.gen(function* () {
      if (Cause.isInterruptedOnly(cause) === true) {
        // console.log('interrupted', Cause.pretty(err), err)
        return
      }

      const span = yield* OtelTracer.currentOtelSpan.pipe(
        Effect.catchTag('NoSuchElementException', (_) => Effect.succeed(undefined)),
      )

      const firstErrLine = cause.toString().split('\n')[0]
      yield* Effect.logError(firstErrLine, cause).pipe((_) =>
        span === undefined
          ? _
          : Effect.annotateLogs({ spanId: span.spanContext().spanId, traceId: span.spanContext().traceId })(_),
      )
    }),
  )

/**
 * Creates a defect, pausing at a breakpoint in development.
 *
 * @param msg - The error message to include in the defect.
 * @param args - Arbitrary arguments available for inspection during debugging.
 *
 * @see {@link shouldNeverHappen} for the non-Effect equivalent that throws synchronously.
 * @see {@link orDieDebugger}
 */
export const dieDebugger = (msg: string, ...args: ReadonlyArray<unknown>): Effect.Effect<never> =>
  Effect.suspend(() => {
    if (isDevEnv() === true) {
      // oxlint-disable-next-line eslint(no-debugger) -- intentional breakpoint during development
      debugger
      void args // Keeps the variable in scope so it's inspectable when the debugger pauses
    }
    return Effect.dieMessage(msg)
  })

/**
 * Converts a failure into a defect, pausing at a breakpoint in development.
 *
 * @param self - The effect on which to apply the operation.
 *
 * @see {@link Effect.orDie}
 * @see {@link dieDebugger}
 */
export const orDieDebugger = <A, E, R>(self: Effect.Effect<A, E, R>): Effect.Effect<A, never, R> =>
  Effect.matchEffect(self, {
    onFailure: (error) =>
      // Keep the debugger hook so that `debugger` runs only when the wrapped effect actually fails, not while building the wrapper.
      Effect.dieSync(() => {
        if (isDevEnv() === true) {
          // oxlint-disable-next-line eslint(no-debugger) -- intentional breakpoint for impossible states during development
          debugger
        }
        return error
      }),
    onSuccess: Effect.succeed,
  })

export const ignoreIf: {
  <E, EB extends E>(
    refinement: Refinement<NoInfer<E>, EB>,
  ): <A, R>(self: Effect.Effect<A, E, R>) => Effect.Effect<void, Exclude<E, EB>, R>
  <E>(predicate: Predicate<NoInfer<E>>): <A, R>(self: Effect.Effect<A, E, R>) => Effect.Effect<void, E, R>
  <A, E, R, EB extends E>(
    self: Effect.Effect<A, E, R>,
    refinement: Refinement<E, EB>,
  ): Effect.Effect<void, Exclude<E, EB>, R>
  <A, E, R>(self: Effect.Effect<A, E, R>, predicate: Predicate<E>): Effect.Effect<void, E, R>
} = dual(2, <A, E, R>(self: Effect.Effect<A, E, R>, predicate: Predicate<E>) =>
  self.pipe(Effect.catchIf(predicate, () => Effect.void)),
)

export const eventListener = <TEvent = unknown>(
  target: Stream.EventListener<TEvent>,
  type: string,
  handler: (event: TEvent) => Effect.Effect<void>,
  options?: { once?: boolean },
) =>
  Effect.gen(function* () {
    const runtime = yield* Effect.runtime()

    const handlerFn = (event: TEvent) => handler(event).pipe(Effect.provide(runtime), Effect.runFork)

    target.addEventListener(type, handlerFn, { once: options?.once ?? false })

    yield* Effect.addFinalizer(() => Effect.sync(() => target.removeEventListener(type, handlerFn)))
  })

export const logWarnIfTakesLongerThan =
  ({ label, duration }: { label: string; duration: Duration.DurationInput }) =>
  <R, E, A>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.gen(function* () {
      const runtime = yield* Effect.runtime()

      let tookLongerThanTimer = false

      const timeoutFiber = Effect.sleep(duration).pipe(
        Effect.tap(() => {
          tookLongerThanTimer = true
          // TODO include span info
          return Effect.logWarning(`${label}: Took longer than ${objectToString(duration)}ms`)
        }),
        Effect.provide(runtime),
        Effect.runFork,
      )

      const start = Date.now()
      const res = yield* eff.pipe(
        Effect.exit,
        Effect.onInterrupt(
          Effect.fn(function* () {
            const end = Date.now()

            yield* Fiber.interrupt(timeoutFiber)

            if (tookLongerThanTimer === true) {
              yield* Effect.logWarning(`${label}: Interrupted after ${end - start}ms`)
            }
          }),
        ),
      )

      // eslint-disable-next-line overeng/explicit-boolean-compare -- mutated in forked fiber; TS can't see the mutation
      if (tookLongerThanTimer) {
        const end = Date.now()
        yield* Effect.logWarning(`${label}: Actual duration: ${end - start}ms`)
      }

      yield* Fiber.interrupt(timeoutFiber)

      return yield* res
    })

export const logDuration =
  (label: string) =>
  <R, E, A>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.gen(function* () {
      const start = Date.now()
      const res = yield* eff
      const end = Date.now()
      yield* Effect.log(`${label}: ${Duration.format(end - start)}`)
      return res
    })

export const tapSync =
  <A>(tapFn: (a: A) => unknown) =>
  <R, E>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.tap(eff, (a) => Effect.sync(() => tapFn(a)))

export const debugLogEnv = (msg?: string): Effect.Effect<Context.Context<never>> =>
  pipe(
    Effect.context<never>(),
    Effect.tap((env) => log(msg ?? 'debugLogEnv', env)),
  )

/**
 * Enforces a time limit on an effect, triggering a defect on timeout.
 *
 * @remarks
 *
 * This function allows you to enforce a time limit on the execution of an
 * effect. If the effect does not complete within the given duration, it dies
 * with a {@link Cause.TimeoutException} as an unchecked defect. Unlike
 * {@link Effect.timeout}, which adds `TimeoutException` to the error channel,
 * this function keeps the error channel unchanged by treating the timeout as
 * a defect.
 *
 * The returned effect will either:
 * - Succeed with the original effect's result if it completes within the
 *   specified duration.
 * - Die with a {@link Cause.TimeoutException} defect if the time limit is exceeded.
 *
 * @see {@link timeoutOrDieMessage} for a version with a custom message.
 * @see {@link Effect.timeout} for a version that raises a `TimeoutException` as a typed error.
 * @see {@link Effect.timeoutFailCause} for a version that raises a custom defect.
 */
export const timeoutOrDie =
  (duration: Duration.DurationInput) =>
  <A, E, R>(self: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.timeoutFailCause(self, {
      duration,
      onTimeout: () => Cause.die(new Cause.TimeoutException()),
    })

/**
 * Enforces a time limit on an effect, triggering a defect with a custom
 * message on timeout.
 *
 * @remarks
 *
 * This function behaves like {@link timeoutOrDie}, but allows you to provide
 * a custom message for the {@link Cause.TimeoutException} defect. This is useful
 * for adding context about which operation timed out, making it easier to
 * diagnose issues in logs or error reports.
 *
 * The returned effect will either:
 * - Succeed with the original effect's result if it completes within the
 *   specified duration.
 * - Die with a {@link Cause.TimeoutException} defect containing the provided
 *   message if the time limit is exceeded.
 *
 * @see {@link timeoutOrDie} for a version without a custom message.
 * @see {@link Effect.timeout} for a version that raises a `TimeoutException` as a typed error.
 * @see {@link Effect.timeoutFailCause} for a version that raises a custom defect.
 */
export const timeoutOrDieMessage =
  (duration: Duration.DurationInput, message: string) =>
  <A, E, R>(self: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.timeoutFailCause(self, {
      duration,
      onTimeout: () => Cause.die(new Cause.TimeoutException(message)),
    })

export const toForkedDeferred = <R, E, A>(
  eff: Effect.Effect<A, E, R>,
): Effect.Effect<Deferred.Deferred<A, E>, never, R | Scope.Scope> =>
  pipe(
    Deferred.make<A, E>(),
    Effect.tap((deferred) =>
      pipe(
        Effect.exit(eff),
        Effect.flatMap((ex) => Deferred.done(deferred, ex)),
        tapCauseLogPretty,
        Effect.forkScoped,
      ),
    ),
  )

export const withPerformanceMeasure =
  (meaureLabel: string) =>
  <R, E, A>(eff: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.acquireUseRelease(
      Effect.sync(() => globalThis.performance.mark(`${meaureLabel}:start`)),
      () => eff,
      () =>
        Effect.sync(() => {
          globalThis.performance.mark(`${meaureLabel}:end`)
          globalThis.performance.measure(meaureLabel, `${meaureLabel}:start`, `${meaureLabel}:end`)
        }),
    )

const getSpanTrace = () => {
  const fiberOption = Fiber.getCurrentFiber()
  if (fiberOption._tag === 'None' || fiberOption.value.currentSpan === undefined) {
    return 'No current fiber'
  }

  return ''
  // const msg = Effect.runSync(
  //   Effect.fail({ message: '' }).pipe(
  //     Effect.withParentSpan(fiberOption.value.currentSpan),
  //     Effect.catchAllCause((cause) => Effect.succeed(cause.toString())),
  //   ),
  // )

  // // remove the first line
  // return msg
  //   .split('\n')
  //   .slice(1)
  //   .map((_) => _.trim().replace('at ', ''))
  //   .join('\n')
}

const logSpanTrace = () => console.log(getSpanTrace())

declare global {
  /** Debug helper: returns the current Effect span trace */
  var getSpanTrace: () => string
  /** Debug helper: logs the current Effect span trace */
  var logSpanTrace: () => void
}

globalThis.getSpanTrace = getSpanTrace
globalThis.logSpanTrace = logSpanTrace
